import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import type { components, paths } from "@/api/types";
import type { Result } from "@/shared/api";
import { apiGet, apiGetPage, nextCursor, PAGE_LIMIT, qk } from "@/shared/api";

export type AdminComment = Result<paths["/api/v1/admin/comments"]["get"]>[number];
export type AdminCommentDetail = Result<
  paths["/api/v1/admin/comments/{comment_id}"]["get"]
>;
export type CommentStatus = components["schemas"]["CommentStatus"];

export interface CommentFilters {
  statuses: CommentStatus[];
  hasReports: boolean | null;
}

/**
 * Очередь модерации (доработки §6.2).
 *
 * Пустой список статусов означает «как решит бэкенд», а решает он в пользу
 * очереди: отзывы с жалобами и задержанные фильтром спама. Присылать сюда все
 * пять статусов по умолчанию было бы неверно — при постмодерации это весь
 * поток отзывов, и очередь перестала бы означать «нужна реакция».
 */
export function useComments({ statuses, hasReports }: CommentFilters) {
  return useInfiniteQuery({
    queryKey: qk.comments({ statuses, hasReports }),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGetPage("/api/v1/admin/comments", {
        query: {
          limit: PAGE_LIMIT,
          ...(statuses.length > 0 ? { status: statuses } : {}),
          ...(hasReports === null ? {} : { has_reports: hasReports }),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
        signal,
      }),
    getNextPageParam: (last) => nextCursor(last.meta),
  });
}

/** Отзыв целиком: он сам, жалобы на него и ответы поддержки — одним запросом. */
export function useComment(id: number, enabled = true) {
  return useQuery({
    queryKey: qk.comment(id),
    enabled,
    queryFn: ({ signal }) =>
      apiGet("/api/v1/admin/comments/{comment_id}", {
        params: { comment_id: id },
        signal,
      }),
  });
}
